import {
  CheckCircle2,
  XCircle,
  GitPullRequest,
  RefreshCw,
  FileText,
} from "lucide-react";
import { GithubIcon as Github } from "@/components/icons";
import { Kicker } from "@/components/ui/kicker";
import { formatRelativeTime } from "@/lib/freshness";
import { cn } from "@/lib/utils";

interface FixAttempt {
  id: string;
  attemptType: string;
  description: string | null;
  prUrl: string | null;
  instructionText: string | null;
  createdAt: string;
}

interface VerificationResult {
  id: string;
  outcome: string;
  createdAt: string;
}

interface FindingFixHistorySectionProps {
  fixAttempts: FixAttempt[];
  verificationResults: VerificationResult[];
}

export function FindingFixHistorySection({
  fixAttempts,
  verificationResults,
}: FindingFixHistorySectionProps) {
  if (fixAttempts.length === 0 && verificationResults.length === 0) return null;

  return (
    <section className="space-y-4">
      <Kicker as="h2">Fix history</Kicker>
      <ul className="divide-y divide-border/60">
        {fixAttempts.map((attempt) => {
          const Icon =
            attempt.attemptType === "github_issue"
              ? Github
              : attempt.attemptType === "code_pr"
                ? GitPullRequest
                : attempt.attemptType === "prototype_iteration"
                  ? RefreshCw
                  : FileText;
          return (
            <li
              key={attempt.id}
              className="flex items-start gap-3 py-3 text-base"
            >
              <Icon
                className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium capitalize">
                    {attempt.attemptType === "github_issue"
                      ? "GitHub issue"
                      : attempt.attemptType.replace(/_/g, " ")}
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {formatRelativeTime(attempt.createdAt)}
                  </span>
                </div>
                {attempt.description && (
                  <p className="text-sm text-muted-foreground">
                    {attempt.description}
                  </p>
                )}
                {attempt.prUrl && (
                  <a
                    href={attempt.prUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary underline underline-offset-4"
                  >
                    View pull request
                  </a>
                )}
              </div>
            </li>
          );
        })}
        {verificationResults.map((result) => {
          const Icon = result.outcome === "resolved" ? CheckCircle2 : XCircle;
          return (
            <li
              key={result.id}
              className="flex items-start gap-3 py-3 text-base"
            >
              <Icon
                aria-hidden="true"
                className={cn(
                  "h-4 w-4 mt-0.5 shrink-0",
                  result.outcome === "resolved"
                    ? "text-chart-1"
                    : "text-destructive",
                )}
              />
              <div className="flex-1 flex items-baseline gap-2">
                <span className="font-medium">
                  Verified{" "}
                  {result.outcome === "resolved" ? "resolved" : "persists"}
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {formatRelativeTime(result.createdAt)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
